// Lightweight global toast system. Custom-event driven so any component can
// fire `toast.success("Sent")` without prop-drilling or context.
//
// Usage:
//   import { toast } from "@/components/Toaster";
//   toast.success("Sent to ethan@example.com");
//   toast.error("Couldn't send: " + msg);
//
// <Toaster /> is mounted once at the workspace root (Shell.tsx).

import { useEffect, useState } from "react";
import { X } from "lucide-react";
import clsx from "clsx";
import { Button } from "@/proton/ui/Button";
import { Icon } from "@/proton/ui/Icon";

export type ToastKind = "success" | "error" | "info";

// Optional inline action (e.g. an "Undo" button on a send toast). onClick fires
// on the client; the caller is responsible for dismissing the toast afterward
// (via the id returned from toast.action, or letting it time out).
export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastPayload {
  id: string;
  kind: ToastKind;
  message: string;
  // Auto-dismiss after this many ms. Default 4500. 0 means manual dismiss only.
  ttl?: number;
  action?: ToastAction;
}

const EVT = "__l7_toast";
const EVT_DISMISS = "__l7_toast_dismiss";

function fire(kind: ToastKind, message: string, ttl?: number, action?: ToastAction): string {
  const detail: ToastPayload = { id: crypto.randomUUID(), kind, message, ttl, action };
  window.dispatchEvent(new CustomEvent<ToastPayload>(EVT, { detail }));
  return detail.id;
}

export const toast = {
  success: (message: string, ttl?: number) => fire("success", message, ttl),
  error:   (message: string, ttl?: number) => fire("error",   message, ttl ?? 8000),
  info:    (message: string, ttl?: number) => fire("info",    message, ttl),
  // Toast carrying an action button. Returns the id so the caller can dismiss it
  // early (e.g. once the action resolves). Defaults to a longer ttl so the user
  // has time to react — pass the undo window as ttl for a matching countdown.
  action:  (kind: ToastKind, message: string, action: ToastAction, ttl?: number) =>
    fire(kind, message, ttl ?? 10000, action),
  // Remove a toast by id (fired from toast.action or toast.success).
  dismiss: (id: string) => window.dispatchEvent(new CustomEvent<string>(EVT_DISMISS, { detail: id })),
};

export function Toaster(): JSX.Element {
  const [items, setItems] = useState<ToastPayload[]>([]);

  useEffect(() => {
    function onToast(e: Event) {
      const t = (e as CustomEvent<ToastPayload>).detail;
      setItems((curr) => [...curr, t]);
      const ttl = t.ttl ?? 4500;
      if (ttl > 0) {
        setTimeout(() => {
          setItems((curr) => curr.filter((x) => x.id !== t.id));
        }, ttl);
      }
    }
    function onDismiss(e: Event) {
      const id = (e as CustomEvent<string>).detail;
      setItems((curr) => curr.filter((x) => x.id !== id));
    }
    window.addEventListener(EVT, onToast as EventListener);
    window.addEventListener(EVT_DISMISS, onDismiss as EventListener);
    return () => {
      window.removeEventListener(EVT, onToast as EventListener);
      window.removeEventListener(EVT_DISMISS, onDismiss as EventListener);
    };
  }, []);

  function dismiss(id: string) {
    setItems((curr) => curr.filter((x) => x.id !== id));
  }

  if (items.length === 0) return <></>;

  // Cap the visible stack at 3 (newest kept); older ones collapse into a count.
  const MAX_VISIBLE = 3;
  const visible = items.slice(-MAX_VISIBLE);
  const hidden = items.length - visible.length;

  // Proton notifications (components/notification): bottom-centre pills with
  // the message, an optional action, and a close button. z-index 1100 keeps
  // errors above modals and the command palette.
  return (
    <div className="notifications-container" aria-live="polite">
      {hidden > 0 && <div className="notification notification--muted">+{hidden} more</div>}
      {visible.map((t) => (
        <div
          key={t.id}
          className={clsx("notification notification--has-close-button", t.kind === "error" && "notification--error")}
          role={t.kind === "error" ? "alert" : "status"}
        >
          <div className="notification__content">
            <span>{t.message}</span>
            {t.action && (
              <Button shape="ghost" size="small" className="notification__button" onClick={() => t.action!.onClick()}>
                {t.action.label}
              </Button>
            )}
          </div>
          <Button
            shape="ghost"
            size="small"
            icon
            className="notification__close-button"
            title="Dismiss"
            aria-label="Dismiss notification"
            onClick={() => dismiss(t.id)}
          >
            <Icon as={X} />
          </Button>
        </div>
      ))}
    </div>
  );
}
